import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { DataTable, type Column } from '../components/DataTable.tsx';
import { Pagination } from '../components/Pagination.tsx';
import { apiFetch } from '../lib/api.ts';
import { money, num } from '../lib/format.ts';

type VarianceStatus =
  | 'match' | 'over' | 'short' | 'counted_not_in_obm' | 'obm_not_counted';

interface VarianceRow {
  sku: string;
  name: string;
  location: string | null;
  /** Physical tags scanned. One carton tag may be many pieces. */
  tags: number;
  /** OBM pieces counted (carton tags expanded). */
  counted: number;
  smartlinkBook: number | null;
  obmOnHand: number | null;
  variance: number;
  price: number | null;
  valueVariance: number | null;
  status: VarianceStatus;
}

interface Summary {
  matched: number;
  over: number;
  short: number;
  countedNotInObm: number;
  obmNotCounted: number;
  linesCounted: number;
  totalCounted: number;
  totalObmOnHand: number;
  netUnitVariance: number;
  netValueVariance: number | null;
}

interface Bill {
  id: number;
  billNo: string | null;
  deptName: string | null;
  type: string;
  status: string;
  createTime: string | null;
}

interface VarianceData {
  bill: Bill;
  rows: VarianceRow[];
  summary: Summary;
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  /** Search hits across all statuses — exceeds `total` when the active filter hides some. */
  searchMatchesAllStatuses: number;
}

const STATUS_LABEL: Record<VarianceStatus, string> = {
  match: 'Match',
  over: 'Over',
  short: 'Short',
  counted_not_in_obm: 'Not in OBM',
  obm_not_counted: 'Not counted',
};

const FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All SKUs' },
  { value: 'variance', label: 'Variance only' },
  { value: 'over', label: 'Over' },
  { value: 'short', label: 'Short' },
  { value: 'counted_not_in_obm', label: 'Counted, not in OBM' },
  { value: 'obm_not_counted', label: 'In OBM, not counted' },
];

const PAGE_SIZE = 100;

export function StockCountVariancePage() {
  const { billId } = useParams<{ billId: string }>();
  const [params, setParams] = useSearchParams();

  // Default 'all': counted-first sorting already floats the interesting rows to
  // the top, and 'variance only' silently hid exact-match SKUs from search.
  const filter = params.get('filter') ?? 'all';
  const search = params.get('q') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? 1));

  const [localSearch, setLocalSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => {
      if (localSearch !== search) update('q', localSearch);
    }, 250);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearch]);
  // Default: status ascending — counted lines first (short → over →
  // counted-not-in-OBM → match), with in-OBM-not-counted last.
  const sort = params.get('sort') ?? 'status:asc';

  const query = new URLSearchParams({
    filter,
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort,
  });
  if (search) query.set('q', search);
  const endpoint = `/api/dashboard/stock-counts/${billId}/variance?${query.toString()}`;

  const { data, isLoading, error } = useQuery({
    queryKey: [endpoint],
    queryFn: () => apiFetch<VarianceData>(endpoint),
    placeholderData: (prev) => prev,
  });

  function update(key: string, value: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.set('page', '1');
      return next;
    });
  }

  function toggleSort(column: string) {
    const [col, dir] = sort.split(':');
    const nextDir = col === column && dir === 'asc' ? 'desc' : 'asc';
    update('sort', `${column}:${nextDir}`);
  }

  const csvHref = (() => {
    const q = new URLSearchParams({ filter, sort, format: 'csv' });
    if (search) q.set('q', search);
    return `/api/dashboard/stock-counts/${billId}/variance?${q.toString()}`;
  })();

  const columns: Column<VarianceRow>[] = [
    {
      key: 'sku',
      label: 'SKU',
      sortable: true,
      className: 'mono',
      render: (r) => <Link to={`/skus/${encodeURIComponent(r.sku)}`}>{r.sku}</Link>,
    },
    { key: 'name', label: 'Name', sortable: true, render: (r) => r.name },
    { key: 'location', label: 'Loc', sortable: true, className: 'mono', render: (r) => r.location ?? '—' },
    // Tags = physical scans; Counted = OBM pieces. One carton tag can be many
    // pieces, so both are shown rather than leaving the expansion invisible.
    { key: 'tags', label: 'Tags', sortable: true, className: 'num', render: (r) => (r.tags ? num(r.tags) : '—') },
    { key: 'counted', label: 'Pieces', sortable: true, className: 'num', render: (r) => num(r.counted) },
    { key: 'smartlink_book', label: 'SL book', sortable: true, className: 'num', render: (r) => num(r.smartlinkBook) },
    { key: 'obm_on_hand', label: 'OBM on hand', sortable: true, className: 'num', render: (r) => num(r.obmOnHand) },
    {
      key: 'variance',
      label: 'Variance',
      sortable: true,
      className: 'num',
      render: (r) => (
        <span
          style={{
            color: r.variance < 0 ? 'var(--danger, #ff6b6b)' : r.variance > 0 ? 'var(--text)' : 'var(--text-muted)',
            fontWeight: r.variance !== 0 ? 600 : 400,
          }}
        >
          {num(r.variance)}
        </span>
      ),
    },
    { key: 'value_variance', label: 'Value var.', sortable: true, className: 'num', render: (r) => money(r.valueVariance) },
    // Sorts on the server's status rank (counted lines first), not alphabetically.
    { key: 'status', label: 'Status', sortable: true, render: (r) => STATUS_LABEL[r.status] },
  ];

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">
          Variance · {data?.bill.billNo ?? billId}
        </h1>
        <span className="main-subtitle">
          {data
            ? `${data.bill.type} · ${data.bill.status}${data.bill.deptName ? ` · ${data.bill.deptName}` : ''} · counted vs OBM on-hand`
            : ''}
        </span>
      </header>

      <p style={{ marginTop: -8 }}>
        <Link to="/stock-counts">← All stock counts</Link>
      </p>

      {error && <div className="error">{(error as Error).message}</div>}
      {isLoading && !data && <div className="loading">Loading…</div>}

      {data && (
        <>
          <div className="card-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, margin: '12px 0' }}>
            <SummaryCard label="Matched" value={num(data.summary.matched)} />
            <SummaryCard label="Over" value={num(data.summary.over)} />
            <SummaryCard label="Short" value={num(data.summary.short)} />
            <SummaryCard label="Counted, not in OBM" value={num(data.summary.countedNotInObm)} />
            <SummaryCard label="In OBM, not counted" value={num(data.summary.obmNotCounted)} />
            <SummaryCard label="Total counted" value={num(data.summary.totalCounted)} />
            <SummaryCard label="Total OBM on-hand" value={num(data.summary.totalObmOnHand)} />
            <SummaryCard label="Net unit variance" value={num(data.summary.netUnitVariance)} />
            <SummaryCard label="Net value variance" value={money(data.summary.netValueVariance)} />
          </div>

          <div className="toolbar">
            <input
              placeholder="Search SKU or name…"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
            <select value={filter} onChange={(e) => update('filter', e.target.value)}>
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <a className="btn" href={csvHref}>
              Export CSV
            </a>
          </div>

          {/* A searched SKU that matched OBM exactly has zero variance, so the
              default 'Variance only' filter hides it — which reads as "this SKU
              wasn't counted". Say what actually happened and offer the fix. */}
          {search && data.total === 0 && data.searchMatchesAllStatuses > 0 && (
            <div className="notice" style={{ margin: '12px 0', padding: '10px 12px', border: '1px solid var(--border, #444)', borderRadius: 6 }}>
              {data.searchMatchesAllStatuses === 1
                ? `“${search}” is in this count but hidden by the “${FILTERS.find((f) => f.value === filter)?.label}” filter — it has no variance.`
                : `${data.searchMatchesAllStatuses} rows match “${search}” but are hidden by the “${FILTERS.find((f) => f.value === filter)?.label}” filter.`}{' '}
              <button type="button" className="btn" onClick={() => update('filter', 'all')} style={{ marginLeft: 8 }}>
                Show in All SKUs
              </button>
            </div>
          )}

          <DataTable
            columns={columns}
            rows={data.rows}
            rowKey={(r) => r.sku}
            sortColumn={sort.split(':')[0]}
            sortDirection={sort.split(':')[1]?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC'}
            onSortChange={toggleSort}
            emptyMessage="No rows match this filter."
          />
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={(p) => update('page', String(p))}
          />
        </>
      )}
    </>
  );
}

function SummaryCard(props: { label: string; value: string }) {
  return (
    <div className="stat-card" style={{ minWidth: 120 }}>
      <div className="stat-label" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {props.label}
      </div>
      <div className="stat-value" style={{ fontSize: 20, fontWeight: 600 }}>
        {props.value}
      </div>
    </div>
  );
}
